import { ReactNode } from "react";
import {
	Alert,
	Box,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useReviewDetail } from "../../hooks/useEventInstance";
import { ReviewDetail } from "@/domain/eventInstance.interface";
import { PhotoViewer } from "./PhotoViewer";
import { FormDisplay } from "./FormDisplay";
import { VideoPlayer } from "./VideoPlayer";
import { CompoundView } from "./CompoundView";
import { GenericPlaceholder } from "./GenericPlaceholder";
import { SubmissionMetadata } from "./SubmissionMetadata";

interface Props {
	open: boolean;
	enrollmentId: string | undefined;
	instanceId: string | undefined;
	onClose: () => void;
	/**
	 * If provided, the modal renders this in place of the default view-only
	 * body. Used by SIMS-4988's review modal to inject sequential nav.
	 */
	renderBody?: (detail: ReviewDetail) => ReactNode;
	/**
	 * Optional footer (e.g. action buttons in review mode).
	 */
	renderActions?: (detail: ReviewDetail) => ReactNode;
}

const renderDefaultBody = (detail: ReviewDetail): ReactNode => {
	if (detail.compoundSteps?.length) {
		return <CompoundView steps={detail.compoundSteps} mode="view" />;
	}
	switch (detail.validationType) {
		case "PHOTOS":
			return <PhotoViewer photos={detail.photos ?? []} mode="view" />;
		case "FORM":
			return (
				<FormDisplay
					formConfig={detail.formConfig}
					responses={detail.formResponses}
					mode="view"
				/>
			);
		case "WATCH_VIDEO":
		case "CAPTURE_VIDEO":
			return <VideoPlayer mode="view" />;
		default:
			return (
				<GenericPlaceholder
					validationType={detail.validationType}
					mode="view"
				/>
			);
	}
};

export const SubmissionDetailsModal = ({
	open,
	enrollmentId,
	instanceId,
	onClose,
	renderBody,
	renderActions,
}: Props) => {
	const { data, isLoading, error } = useReviewDetail(enrollmentId, instanceId);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
			<DialogTitle sx={{ pr: 6 }}>
				{data?.eventName ?? "Submission Details"}
				<IconButton
					onClick={onClose}
					sx={{ position: "absolute", right: 8, top: 8 }}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers sx={{ p: 0 }}>
				{isLoading && (
					<Stack alignItems="center" sx={{ p: 6 }}>
						<CircularProgress />
					</Stack>
				)}
				{error && (
					<Alert severity="error" sx={{ m: 2 }}>
						Failed to load submission details.
					</Alert>
				)}
				{data && (
					<Box>
						{renderBody ? renderBody(data) : renderDefaultBody(data)}
						<SubmissionMetadata detail={data} />
						{renderActions?.(data)}
					</Box>
				)}
			</DialogContent>
		</Dialog>
	);
};
